/**
 * The AI reels plan of a song ("X days of AI reels", spec 2026-10-12): the rollout kind `reels`.
 * The artist picks a start day, a length in days, a cadence and the posts a day (1, 2 or 3; owner
 * decision 2026-10-19). The steps of the plan are made from these settings, so the kind has no table
 * in `releaseRollout.ts`. Each post of a day is its own AI reel and one post on each channel.
 *
 * The day key carries the start day and the offset (`20261014-d4`). So the rollout key
 * (`reels.20261014-d4`) gives its offset with no settings, and a later plan never has the key of an
 * older plan with another start day. The FIRST post of a day keeps that key, so every stored plan
 * stays valid. The second and the third post of a day add `p2` and `p3` (`20261014-d4p2`).
 *
 * A campaign has no reel limit of its own (owner decision 2026-10-19). Two limits apply: the video
 * credits (one for each reel; the plan credits first, then the packs) and the monthly scheduled
 * posts of the plan (`scheduledPostsPerMonth`), which a post takes when it is scheduled, not as a
 * draft. The plan table holds only the most posts a day (`reelsPlanMaxPostsPerDay`). The settings
 * cannot pass the technical limit `REELS_PLAN_MAX_TOTAL_REELS`.
 *
 * The file has no heavy import, so every project can use it. music24-ios and music24-android hold a
 * copy of the day rule; their parity tests read `__fixtures__/reelsPlanDays.json`.
 */
import { AI_REEL_ROLLOUT_VARIANT, type RolloutDay, type RolloutDayKey } from './releaseRollout';

export type ReelsPlanCadence = 'daily' | 'every2Days' | 'threeAWeek';
export const REELS_PLAN_CADENCES: ReadonlyArray<ReelsPlanCadence> = ['daily', 'every2Days', 'threeAWeek'];
/** The posts of one plan day. Each post is its own AI reel. */
export type ReelsPlanPostsPerDay = 1 | 2 | 3;
export const REELS_PLAN_POSTS_PER_DAY: ReadonlyArray<ReelsPlanPostsPerDay> = [1, 2, 3];
/**
 * The post hours of a day in the time zone of the artist (`AccountModel.timeZone`; owner decision
 * 2026-10-19). One post a day has no fixed hour: it keeps the slot of the channel
 * (`rolloutPostTime`). The index is the place of the post in its day (0 first).
 */
export const REELS_PLAN_POST_HOURS: Readonly<Record<2 | 3, ReadonlyArray<number>>> = {
  2: [10, 18],
  3: [9, 13, 19],
};
export const REELS_PLAN_MIN_DAYS = 3;
export const REELS_PLAN_MAX_DAYS = 60;
/** The technical limit of one campaign: 3 posts a day on each of the 60 days. It is no plan limit, and valid settings never pass it. */
export const REELS_PLAN_MAX_TOTAL_REELS = 3 * REELS_PLAN_MAX_DAYS;
/** The lengths that the setup offers as buttons. */
export const REELS_PLAN_DAY_PRESETS: ReadonlyArray<number> = [7, 14, 30];
/** What a reel of the plan leads with. It rotates by the place of the reel in the plan. */
export type ReelsPlanAngle = 'lyrics' | 'energy' | 'mood';

export interface ReelsPlanSettings {
  /** `YYYY-MM-DD`, in the time zone of the artist. */
  startDay: string;
  days: number;
  cadence: ReelsPlanCadence;
  /** 1, 2 or 3. A stored plan from before the setting reads as 1. */
  postsPerDay: ReelsPlanPostsPerDay;
}

/** The settings as `PromoCampaignModel.planSteps.reelsPlan` holds them. */
export interface StoredReelsPlan extends ReelsPlanSettings {
  /** ISO. */
  madeAt: string;
  byUserId: number;
  /** ISO, set by "End the plan". */
  endedAt?: string;
}

/**
 * Why "Make the plan" is not possible now. The first reason that applies. Short credits give
 * `notEnoughCredits`: the clients offer the credit packs (the plan offer on Free and the trial).
 * The monthly scheduled posts never refuse a plan: its drafts cost no unit.
 */
export type ReelsPlanRefusal =
  /** The posts a day are above `reelsPlanMaxPostsPerDay` of the plan (the trial: 1). The clients open the plan offer. */
  | 'postsPerDayNeedsPlan'
  | 'planExists'
  | 'needsPlan'
  | 'noMaster'
  | 'masterProcessing'
  | 'notEnoughCredits'
  | 'noChannels'
  | 'unavailable';

/** The dry run of `POST /accounts/:accountId/release-kits/:catalogItemUuid/reels-plan`. */
export interface ReelsPlanPreview {
  /** One entry for each post (each reel), by day and then by post. `post` is 1, 2 or 3. */
  days: Array<{ key: string; date: string; post: number }>;
  reelCount: number;
  /** The most posts a day of the workspace plan (`reelsPlanMaxPostsPerDay`). */
  maxPostsPerDay: number;
  /** The credits that are missing; 0 when the plan credits and the packs pay for every reel. */
  missingCredits: number;
  /** The scheduled posts of the plan this month (`scheduledPostsPerMonth`); null for no limit. */
  scheduledPostsLimit: number | null;
  /**
   * The scheduled-post units left this month; null for no limit. Information only: the drafts of
   * the campaign cost no unit, and the launch reports `monthLimit` for a post above the quota.
   */
  scheduledPostsLeft: number | null;
  credits: number;
  postCount: number;
  /** Plan credits left plus pack credits; null for a plan with no limit. */
  creditsLeft: number | null;
  hookCandidates: number;
  canMake: boolean;
  refusal: ReelsPlanRefusal | null;
}

/** The 201 answer of `POST .../reels-plan`. */
export interface ReelsPlanMade {
  campaignUuid: string;
  reels: Array<{ rolloutKey: string; reelUuid: string }>;
}

/** The answer of `DELETE .../reels-plan` ("End the plan"). */
export interface ReelsPlanEnded {
  canceled: number;
  deleted: number;
}

/** On `ReleasePlan` (`GET /releases/:uuid/plan?kind=reels`). */
export interface ReleaseReelsPlan extends ReelsPlanSettings {
  active: boolean;
  lastDay: string;
  reelCount: number;
}

const DAY_MS = 86_400_000;
const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_KEY_PATTERN = /^(\d{4})(\d{2})(\d{2})-d(\d{1,3})(?:p([23]))?$/;
/** The offsets of "3 a week" inside each block of 7 days. */
const THREE_A_WEEK_OFFSETS: ReadonlyArray<number> = [0, 2, 4];
const ANGLES: ReadonlyArray<ReelsPlanAngle> = ['lyrics', 'energy', 'mood'];

/** The UTC start of a `YYYY-MM-DD` day in ms, or null for a text that is not a real day. */
function dayMs(day: string): number | null {
  const match = DAY_PATTERN.exec(day);
  if (match === null) {
    return null;
  }
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  // `Date.UTC` rolls 2026-02-31 over to March: the day is real only when it reads back the same.
  return new Date(ms).toISOString().slice(0, 10) === day ? ms : null;
}

/** `day` plus `offsetDays` as `YYYY-MM-DD` (UTC calendar days). A text that is no day stays as it is. */
function addDays(day: string, offsetDays: number): string {
  const ms = dayMs(day);
  return ms === null ? day : new Date(ms + offsetDays * DAY_MS).toISOString().slice(0, 10);
}

/** every day 0,1,2…; every 2 days 0,2,4…; 3 a week 0,2,4 of each block of 7. Only offsets < days. */
export function reelsPlanOffsets(settings: Pick<ReelsPlanSettings, 'days' | 'cadence'>): number[] {
  const { days, cadence } = settings;
  const offsets: number[] = [];
  if (cadence === 'threeAWeek') {
    for (let block = 0; block < days; block += 7) {
      THREE_A_WEEK_OFFSETS.forEach((offset) => {
        if (block + offset < days) {
          offsets.push(block + offset);
        }
      });
    }
    return offsets;
  }
  const step = cadence === 'every2Days' ? 2 : 1;
  for (let offset = 0; offset < days; offset += step) {
    offsets.push(offset);
  }
  return offsets;
}

/** The reels (and posts on each channel) of a plan: the post days times the posts a day. */
export function reelsPlanReelCount(
  settings: Pick<ReelsPlanSettings, 'days' | 'cadence'> & { postsPerDay?: ReelsPlanPostsPerDay }
): number {
  return reelsPlanOffsets(settings).length * (settings.postsPerDay ?? 1);
}

/**
 * The shortest length in days that gives exactly `reelCount` reels (posts) with this cadence and
 * these posts a day, or null when no length from REELS_PLAN_MIN_DAYS to REELS_PLAN_MAX_DAYS gives
 * that count (for example 2 reels every day, or 3 reels with 2 posts a day).
 */
export function reelsPlanDaysForCount(
  reelCount: number,
  cadence: ReelsPlanCadence,
  postsPerDay: ReelsPlanPostsPerDay = 1
): number | null {
  if (!Number.isInteger(reelCount) || reelCount < 1) {
    return null;
  }
  for (let days = REELS_PLAN_MIN_DAYS; days <= REELS_PLAN_MAX_DAYS; days += 1) {
    const count = reelsPlanReelCount({ days, cadence, postsPerDay });
    if (count === reelCount) {
      return days;
    }
    if (count > reelCount) {
      return null;
    }
  }
  return null;
}

/**
 * `20261014-d4` for start day 2026-10-14, offset 4 and the first post of the day. The second and
 * the third post add `p2` and `p3` (`20261014-d4p2`). No dot in it.
 */
export function reelsPlanDayKey(startDay: string, offsetDays: number, post = 1): string {
  const base = `${startDay.slice(0, 10).replace(/-/g, '')}-d${offsetDays}`;
  return post > 1 ? `${base}p${post}` : base;
}

/**
 * `{ startDay: '2026-10-14', offsetDays: 4, post: 1 }` (`post` is 1 for a key with no suffix), or
 * null for a key of another form.
 */
export function parseReelsPlanDayKey(dayKey: string): { startDay: string; offsetDays: number; post: number } | null {
  const match = DAY_KEY_PATTERN.exec(dayKey);
  if (match === null) {
    return null;
  }
  const startDay = `${match[1]}-${match[2]}-${match[3]}`;
  if (dayMs(startDay) === null) {
    return null;
  }
  return { startDay, offsetDays: Number(match[4]), post: match[5] === undefined ? 1 : Number(match[5]) };
}

/**
 * The fixed post hour (0..23, the time zone of the artist) of a post of a plan step, or null when
 * the step keeps the slot of the channel: a plan with 1 post a day, or a key of another form.
 */
export function reelsPlanPostHour(postsPerDay: ReelsPlanPostsPerDay, dayKey: string): number | null {
  if (postsPerDay === 1) {
    return null;
  }
  const parsed = parseReelsPlanDayKey(dayKey);
  if (parsed === null) {
    return null;
  }
  return REELS_PLAN_POST_HOURS[postsPerDay][parsed.post - 1] ?? null;
}

/**
 * One `RolloutDay` for each post: by offset, then by post (1 to `postsPerDay`). Format 'reel',
 * asset aiReel / AI_REEL_ROLLOUT_VARIANT, isTask false. The posts of one day share `offsetDays`.
 */
export function reelsPlanDays(
  settings: Pick<ReelsPlanSettings, 'startDay' | 'days' | 'cadence'> & { postsPerDay?: ReelsPlanPostsPerDay }
): RolloutDay[] {
  const postsPerDay = settings.postsPerDay ?? 1;
  return reelsPlanOffsets(settings).flatMap((offsetDays) =>
    Array.from({ length: postsPerDay }, (_unused, index) => ({
      key: reelsPlanDayKey(settings.startDay, offsetDays, index + 1) as RolloutDayKey,
      offsetDays,
      asset: { themeId: 'aiReel' as const, variant: AI_REEL_ROLLOUT_VARIANT },
      format: 'reel' as const,
      isTask: false,
    }))
  );
}

/** The calendar day of one offset of the plan (`YYYY-MM-DD`): the start day plus the offset. */
export function reelsPlanDateOf(startDay: string, offsetDays: number): string {
  return addDays(startDay.slice(0, 10), offsetDays);
}

/** The last day of the plan (`YYYY-MM-DD`): the start day plus the largest offset. */
export function reelsPlanLastDay(settings: Pick<ReelsPlanSettings, 'startDay' | 'days' | 'cadence'>): string {
  const offsets = reelsPlanOffsets(settings);
  return reelsPlanDateOf(settings.startDay, offsets[offsets.length - 1] ?? 0);
}

/** Active: not ended, and `today` (`YYYY-MM-DD`) is not after the last day. */
export function isReelsPlanActive(plan: StoredReelsPlan, today: string): boolean {
  return plan.endedAt === undefined && today.slice(0, 10) <= reelsPlanLastDay(plan);
}

/**
 * Null for a bad value: not a day, days outside 3..60, an unknown cadence, posts a day other than
 * 1, 2 or 3. No `postsPerDay` reads as 1 (a stored plan from before the setting, an old client).
 * Does not check the cap.
 */
export function parseReelsPlanSettings(value: unknown): ReelsPlanSettings | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const { startDay, days, cadence, postsPerDay } = value as Record<string, unknown>;
  if (typeof startDay !== 'string' || dayMs(startDay) === null) {
    return null;
  }
  if (typeof days !== 'number' || !Number.isInteger(days) || days < REELS_PLAN_MIN_DAYS || days > REELS_PLAN_MAX_DAYS) {
    return null;
  }
  const known = REELS_PLAN_CADENCES.find((one) => one === cadence);
  if (known === undefined) {
    return null;
  }
  const posts =
    postsPerDay === undefined || postsPerDay === null ? 1 : REELS_PLAN_POSTS_PER_DAY.find((one) => one === postsPerDay);
  if (posts === undefined) {
    return null;
  }
  return { startDay, days, cadence: known, postsPerDay: posts };
}

/** The stored plan of `planSteps.reelsPlan`, or null for a value of another shape. Pure. */
export function parseStoredReelsPlan(value: unknown): StoredReelsPlan | null {
  const settings = parseReelsPlanSettings(value);
  if (settings === null) {
    return null;
  }
  const { madeAt, byUserId, endedAt } = value as Record<string, unknown>;
  if (typeof madeAt !== 'string' || typeof byUserId !== 'number') {
    return null;
  }
  return { ...settings, madeAt, byUserId, ...(typeof endedAt === 'string' ? { endedAt } : {}) };
}

/** index 0 → 'lyrics', 1 → 'energy', 2 → 'mood', then again. */
export function reelsPlanAngle(index: number): ReelsPlanAngle {
  const place = ((Math.trunc(index) % ANGLES.length) + ANGLES.length) % ANGLES.length;
  return ANGLES[place] ?? 'lyrics';
}

/** The English sentence that the server adds to the Instructions of the reel. */
export function reelsPlanAngleInstruction(angle: ReelsPlanAngle): string {
  switch (angle) {
    case 'lyrics':
      return 'Lead with the lyrics: let the captions quote the lyrics of this part of the song.';
    case 'energy':
      return 'Lead with the energy: use fast cuts on the strongest moment of this part of the song.';
    case 'mood':
      return 'Lead with the mood: use slow shots and few words.';
  }
}

/** True when reelCount > 2 × hookCandidates (the "can repeat parts" note). */
export function reelsPlanCanRepeat(reelCount: number, hookCandidates: number): boolean {
  return reelCount > 2 * hookCandidates;
}
